import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import useAppContext from '../../../hooks/useAppContext';
import { toast } from 'react-toastify';
import { AppContextType } from '../../../context/AppContext';

type ExitDraftModalProps = {
  defaultEmail?: string,
  isOpen: boolean;
  onCancel: () => void;
  onSaveDraft: (email: string) => void;
  status: string,
  onSuccess?: () => void; // ✅ Toujours optionnel
};

const ExitDraftModal: React.FC<ExitDraftModalProps> = ({
  defaultEmail,
  isOpen,
  onCancel,
  onSaveDraft,
  status,
  onSuccess 
}) => {
  const [email, setEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const { draftFormData } = useAppContext() as AppContextType;

  useEffect(() => {
    if (!isOpen) {
      setMessage('');
      setIsSuccess(false);
      setIsLoading(false);
      return;
    }

    if (status === "anonymous") {
      setEmail("");
    } else if (defaultEmail) {
      setEmail(defaultEmail);
    }
  }, [isOpen, status, defaultEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setMessage('Veuillez saisir une adresse email valide.');
        setIsSuccess(false);
        setIsLoading(false);
        return;
      }
      
      setMessage('Brouillon en cours de sauvegarde...');
      setIsSuccess(true);
      
      // ✅ Exécuter la sauvegarde
      await onSaveDraft(email);
      
      // ✅ SIMPLIFIÉ : Toujours appeler onSuccess s'il existe
      // C'est le parent (DraftSaveButton) qui décide quoi faire avec wasAnonymous
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
        onCancel();
      }, 1000); // Réduit à 1 seconde pour une meilleure UX
      
    } catch (error: any) {
      console.error('Erreur dans ExitDraftModal:', error);
      setMessage('Une erreur est survenue lors de la sauvegarde.');
      setIsSuccess(false);
      toast.error("Erreur lors de la sauvegarde du brouillon");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>Sauvegarder votre annonce</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message && (
          <Alert variant={isSuccess ? 'success' : 'danger'}>
            {message}
          </Alert>
        )}
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Entrez votre email pour recevoir un lien de reprise</Form.Label>
            <Form.Control
              type="email"
              placeholder="Votre email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
            <Form.Text className="text-muted">
              Nous vous enverrons un lien pour reprendre votre annonce plus tard.
            </Form.Text>
          </Form.Group>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button 
              variant="outline-secondary" 
              onClick={onCancel}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={isLoading}
              style={{ backgroundColor: '#2A9D8F', borderColor: '#2A9D8F' }}
            >
              {isLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  Sauvegarde...
                </>
              ) : 'Sauvegarder'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ExitDraftModal;